import { Info } from 'lucide-react'
import { Toggle, Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@atrvd/saas-ui'
import { CONNECTION_COPY } from '@/components/ledgersync/connectionManagement.utils'
import { ReadOnlyTooltip } from './ReadOnlyControls'

interface CashPositionToggleProps {
  /** Names the switch: "Include <accountName> in Cash Position". */
  accountName: string
  /** The stored `include_in_cash_position`. */
  checked: boolean
  /** A credit card: never included, so the switch is off, disabled and says why. */
  locked: boolean
  /** A write for this account is in flight. */
  busy: boolean
  /** The page is an inactive facility's. */
  readOnly: boolean
  onChange: (include: boolean) => void
}

/**
 * The "Include in Cash Position" cell of a Financial Accounts table — the same
 * control for connected and manual accounts.
 *
 * A credit card always reads off: its balance is a liability, and the read
 * models exclude cards whatever the stored flag says (ADR 0017), so showing a
 * stale `true` would claim an inclusion that never happens.
 */
export function CashPositionToggle({
  accountName,
  checked,
  locked,
  busy,
  readOnly,
  onChange,
}: CashPositionToggleProps) {
  const on = checked && !locked
  // The Yes/No text beside the switch is the prototype's, and it earns its
  // place: a switch alone encodes state only in colour and knob position, which
  // is the one cue that does not survive a greyed-out control — and a credit
  // card's switch is always greyed out. `aria-hidden` because the switch
  // already carries the state to assistive tech through `aria-label` + checked;
  // without it a screen reader reads the value twice.
  const toggle = (
    <span className="inline-flex items-center gap-2">
      <Toggle
        checked={on}
        disabled={locked || busy || readOnly}
        size="sm"
        aria-label={`Include ${accountName} in Cash Position`}
        onChange={onChange}
      />
      <span aria-hidden="true" className="text-sm text-muted-foreground">
        {on ? 'Yes' : 'No'}
      </span>
    </span>
  )
  // A bare disabled control reads as a bug, so a card's switch says why.
  if (!locked) return <ReadOnlyTooltip readOnly={readOnly}>{toggle}</ReadOnlyTooltip>
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-not-allowed items-center gap-1.5">
          {toggle}
          <Info aria-hidden="true" className="h-3.5 w-3.5 text-muted-foreground" />
        </span>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent>{CONNECTION_COPY.cashPositionCreditCard}</TooltipContent>
      </TooltipPortal>
    </Tooltip>
  )
}
